import React, { useState, useEffect, useCallback } from 'react';
import { Table, Tag, Button, Space, Tooltip, Popconfirm, message } from 'antd';
import { EditOutlined, StopOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { User } from '../../interfaces/userManagement';
import orgManagementService from '../../apiServices/orgManagementService';
import { useAuthOrg } from '../../context/AuthOrgContext';
import '../../assets/css/tablesShadow.css'
import { getRoleLabel, getRoleTagColor } from '../../utils/roles';

interface UserListTableProps {
    refreshTrigger: number;
    onEdit: (user: User) => void;
    onDeactivate: (userId: string) => void;
    onReactivate: (userId: string) => void;
}

const UserListTable: React.FC<UserListTableProps> = ({
    refreshTrigger,
    onEdit,
    onDeactivate,
    onReactivate,
}) => {
    const { currentOrg } = useAuthOrg();
    const [users, setUsers] = useState<User[]>([]);
    const [loading, setLoading] = useState(false);

    const fetchUsers = useCallback(async () => {
        if (!currentOrg) return;
        setLoading(true);
        try {
            const response = await orgManagementService.fetchUsers();
            // Map response data to User interface
            const userList: User[] = response.data.map(u => ({
                id: u.id.toString(),
                userName: u.username,
                userEmail: u.emailId,
                userRole: u.userRole as User['userRole'],
                userType: u.userType as 'internal' | 'external',
                userMode: u.userMode as 'self-served' | 'managed',
                status: 'active', // Default status as API doesn't return it yet
                lastActive: u.updatedAt,
                mobile: u.mobileNumber,
                viewPiiAccess: Boolean(u.viewPiiAccess ?? u.view_pii_access),
                joinedDate: u.createdAt,
                accessibleOrgs: u.accessibleOrgs?.map(org => ({
                    orgId: org.orgId,
                    orgName: org.orgName,
                    parentOrg: org.parentOrg
                }))
            }));
            setUsers(userList);
        } catch (error) {
            console.error('Error fetching users:', error);
            message.error('Failed to load users');
        } finally {
            setLoading(false);
        }
    }, [currentOrg]);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers, refreshTrigger]);
    const columns = [
        {
            title: 'Name',
            dataIndex: 'userName',
            key: 'userName',
            render: (text: string, record: User) => (
                <div className="flex flex-col">
                    <span className="font-medium">{text}</span>
                    <span className="text-xs text-gray-500">{record.userEmail}</span>
                </div>
            ),
        },
        {
            title: 'Role',
            dataIndex: 'userRole',
            key: 'userRole',
            render: (text: string, record: User) => {
                return (
                    <Tag color={getRoleTagColor(record.userRole)}>
                        {getRoleLabel(record.userRole) || '-'}
                    </Tag>
                );
            },
        },
        {
            title: 'Accessible Accounts',
            dataIndex: 'accessibleOrgs',
            key: 'accessibleOrgs',
            render: (_: any, record: User) => {
                const orgs = record.accessibleOrgs || [];
                if (orgs.length === 0) return '-';

                const visibleOrgs = orgs.slice(0, 2);
                const remainingCount = orgs.length - visibleOrgs.length;

                return (
                    <Space size={[0, 4]} wrap>
                        {visibleOrgs.map((org) => (
                            <Tag key={org.orgId}>{org.orgName}</Tag>
                        ))}
                        {remainingCount > 0 && <Tag>+{remainingCount}</Tag>}
                    </Space>
                );
            },
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status: string) => {
                let color = 'green';
                if (status === 'inactive') color = 'red';
                if (status === 'pending') color = 'orange';
                return (
                    <Tag color={color}>
                        {status?.toUpperCase() || '-'}
                    </Tag>
                );
            },
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_: any, record: User) => (
                <Space size="middle">
                    <Tooltip title="Edit User">
                        <Button
                            type="text"
                            icon={<EditOutlined />}
                            onClick={() => onEdit(record)}
                        />
                    </Tooltip>
                    {record.userMode === 'self-served' ? (
                        <Popconfirm
                            title="Deactivate User"
                            description="Are you sure you want to deactivate this user?"
                            onConfirm={() => onDeactivate(record.id)}
                            okText="Yes"
                            cancelText="No"
                        >
                            <Tooltip title="Deactivate">
                                <Button type="text" danger icon={<StopOutlined />} />
                            </Tooltip>
                        </Popconfirm>
                    ) : (
                        <Popconfirm
                            title="Reactivate User"
                            description="Are you sure you want to reactivate this user?"
                            onConfirm={() => onReactivate(record.id)}
                            okText="Yes"
                            cancelText="No"
                        >
                            <Tooltip title="Reactivate">
                                <Button type="text" className="text-green-600" icon={<CheckCircleOutlined />} />
                            </Tooltip>
                        </Popconfirm>
                    )}
                </Space>
            ),
        },
    ];

    return (
        <Table
            columns={columns}
            dataSource={users}
            rowKey="id"
            loading={loading}
            pagination={{ pageSize: 10 }}
        />
    );
};

export default UserListTable;
